/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { bridgeValues } from '../themes/TimeComponentStyles.css';
import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { xUnits } from '#UNSAFE_Theme/utils';

const timeComponentUnresolvedVars: Parameters<typeof useCssVars>[0] = [
  {
    tokenVar: bridgeValues.horizontalTimeAxisHeight,
    key: 'defaultTimeAxisHeight',
    cssProp: 'height'
  },
  {
    tokenVar: bridgeValues.horizontalTimeAxisLabelPaddingInlineStart,
    key: 'timeAxisLabelPaddingInlineStart',
    cssProp: 'margin-inline-start'
  },
  {
    tokenVar: bridgeValues.horizontalTimeAxisLabelPaddingInlineEnd,
    key: 'timeAxisLabelPaddingInlineEnd',
    cssProp: 'margin-inline-end'
  },
  {
    tokenVar: xUnits(12.5),
    key: 'timeAxisLabelMinWidth',
    cssProp: 'min-width'
  },
  {
    tokenVar: typographyVars.body.md.fontSize,
    key: 'majorAxisFontSize',
    cssProp: 'font-size'
  },
  {
    tokenVar: typographyVars.body.sm.fontSize,
    key: 'minorAxisFontSize',
    cssProp: 'margin-block-start'
  },
  {
    tokenVar: typographyVars.body.md.fontWeight.semibold,
    key: 'majorAxisFontWeight'
  },
  {
    tokenVar: typographyVars.body.sm.fontWeight.regular,
    key: 'minorAxisFontWeight'
  }
];

/**
 * Gets the resolved time axis related CSS vars.
 * @param resolvedVars The resolved CSS vars.
 * @returns The resolved values.
 */
const getResolvedTimeAxisVars = (resolvedVars: ReturnType<typeof useCssVars>['resolvedVars']) => ({
  defaultTimeAxisHeight: pxToNumber(resolvedVars['defaultTimeAxisHeight']),
  timeAxisLabelPaddingInlineStart: pxToNumber(resolvedVars['timeAxisLabelPaddingInlineStart']),
  timeAxisLabelPaddingInlineEnd: pxToNumber(resolvedVars['timeAxisLabelPaddingInlineEnd']),
  timeAxisLabelMinWidth: pxToNumber(resolvedVars['timeAxisLabelMinWidth']),
  majorAxisFont: {
    fontSize: resolvedVars['majorAxisFontSize'],
    fontWeight: resolvedVars['majorAxisFontWeight']
  },
  minorAxisFont: {
    fontSize: resolvedVars['minorAxisFontSize'],
    fontWeight: resolvedVars['minorAxisFontWeight']
  }
});

// TODO: Export this from useCssVars? Or make useCssVars output number for px strings?
/**
 * Parses the number from a pixel string.
 * @param length The pixel string.
 * @returns The number.
 */
const pxToNumber = (length: string) =>
  length === '0' ? 0 : Number(length.substring(0, length.length - 2));

export { timeComponentUnresolvedVars, getResolvedTimeAxisVars, pxToNumber };
